import { ReactElement, useEffect } from "react";
import cn from "classnames";
import { Button as AntButton } from "antd";

import { ReactComponent as ArrowRightSVG } from "@/assets/icons/feather-icons/chevron-right.svg";
import { ReactComponent as ArrowLeftSVG } from "@/assets/icons/feather-icons/chevron-left.svg";

import { ButtonColorType, ButtonSize, IButton } from "./types";

import styles from "./style.module.css";

function respondsToEnter(element: HTMLElement) {
  const tagName = element.tagName.toLowerCase();
  const type = element.getAttribute("type")?.toLowerCase() ?? "";

  if (tagName === "textarea" || (tagName === "a" && element.hasAttribute("href"))) {
    return true;
  }

  if (tagName === "input" && (type === "text" || type === "submit" || type === "button")) {
    return true;
  }

  if (tagName === "button") {
    return true;
  }

  if (element.getAttribute("role") === "button") {
    return true;
  }

  return false;
}

export interface IBaseButton extends IButton {
  htmlType: "submit" | "button";
  /** Associate button with a form by form id (for submit buttons rendered outside the form) */
  form?: string;
}

export function BaseButton({
  children,
  icon,
  rightIcon,
  arrowBefore,
  arrowAfter,
  onClick,
  disabled = false,
  ariaDisabled = false,
  isLoading = false,
  fullWidth,
  colorType = ButtonColorType.dark,
  buttonSize = ButtonSize.large,
  style,
  className,
  id,
  isEnabledKeydown = false,
  "aria-label": ariaLabel,
  htmlType,
  form,
}: IBaseButton): ReactElement<any, any> {
  const getStyle = () => (fullWidth ? { width: "100%", ...style } : { ...style });

  useEffect(() => {
    if (!isEnabledKeydown || !id) return;

    const rootEl = document.getElementById("root")!;
    const listener = (event: any) => {
      const buttonEl = document.getElementById(id); // antd doesn't accommodate ref props
      if ((event.code === "Enter" || event.code === "NumpadEnter") && buttonEl && !respondsToEnter(event.target)) {
        event.preventDefault();
        return buttonEl.click();
      }
    };

    // This won't work in modals, since they are rendered outside of the React root by design.
    // This is an acceptable tradeoff, since eventually the candidate experience will be a form,
    // which has this behavior out-of-the-box.
    rootEl.addEventListener("keydown", listener);

    return () => rootEl.removeEventListener("keydown", listener);
  }, [isEnabledKeydown, id]);

  const hasIcon = Boolean(icon);
  const hasChildren = Boolean(children);

  return (
    <AntButton
      id={id}
      type="primary"
      htmlType={htmlType}
      form={form}
      icon={icon}
      onClick={onClick}
      disabled={disabled}
      aria-disabled={ariaDisabled}
      aria-label={ariaLabel}
      loading={isLoading}
      style={getStyle()}
      className={cn(styles.buttonContainer, className, {
        [styles.buttonLarge]: buttonSize === ButtonSize.large,
        [styles.buttonSmall]: buttonSize === ButtonSize.small,
        [styles.buttonXSmall]: buttonSize === ButtonSize.XSmall,
        [styles.buttonXXSmall]: buttonSize === ButtonSize.XXSmall,
        [styles.light]: colorType === ButtonColorType.light,
        [styles.dark]: colorType === ButtonColorType.dark,
        [styles.transparentDark]: colorType === ButtonColorType.transparentDark,
        [styles.transparentLight]: colorType === ButtonColorType.transparentLight,
      })}
    >
      {arrowBefore && <ArrowLeftSVG aria-hidden="true" />}
      {hasChildren && (
        <span
          className={cn(styles.buttonTitle, {
            [styles.withIconBefore]: arrowBefore || hasIcon,
            [styles.withIconAfter]: Boolean(arrowAfter) || Boolean(rightIcon),
          })}
        >
          {children}
        </span>
      )}
      {arrowAfter && <ArrowRightSVG aria-hidden="true" />}
      {Boolean(rightIcon) && rightIcon}
    </AntButton>
  );
}
